import './ProjectListFilterDropdown.scss';
import { BuildingType, CountryCode } from '@belimo-retrofit-portal/logic';
import { FormController } from '@form-ts/core';
import { identity, pipe } from 'fp-ts/function';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button } from 'src/modules/common/components/Button';
import { ProjectStatusTag } from 'src/modules/common/components/ProjectStatusTag';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { HelperText01 } from 'src/modules/common/typography/HelperText01';
import { FormError } from 'src/modules/form/types/FormError';
import { getLoggedInUser } from 'src/modules/login/selectors/getLoggedInUser';
import { getProjectListFilterValues } from 'src/modules/project-list/selectors/getProjectListFilterValues';
import { ProjectListFilter } from 'src/modules/project-list/types/ProjectListFilter';
import { ProjectListFilterGroup } from 'src/modules/project-list/views/ProjectListFilterGroup';

type Props = {
  readonly form: FormController<ProjectListFilter, FormError>;
  readonly onApply: () => void;
  readonly onClose: () => void;
};

export const ProjectListFilterDropdown = ({ form, onApply, onClose }: Props): React.ReactElement => {
  const intl = useIntl();

  const authUser = useSelector(getLoggedInUser);
  const values = useSelector(getProjectListFilterValues);

  const fullNames: Record<string, string> = useMemo(() => values.ownership.reduce((prev, curr) => ({
    ...prev,
    [curr.email]: `${curr.firstName} ${curr.lastName}`,
  }), {}), [values.ownership]);

  const handleClear = useCallback(() => {
    const formValues = form.values.get(form.currentState);
    form.change(pipe(
      form.currentState,
      form.values.set({
        term: formValues.term,

        status: new Set(),

        buildingType: new Set(),

        country: new Set(),
        city: new Set(),

        ratingActual: new Set(),
        ratingFuture: new Set(),

        ownership: new Set(),
      }),
    ));

    onApply();
  }, [form, onApply]);

  // the first group is always here since country and building type are required
  const hasEfficiencyFilter = (
    values.ratingActual.length > 0 ||
    values.ratingFuture.length > 0
  );

  return (
    <div className="bp-project-list-filter-dropdown">
      <div className="bp-project-list-filter-dropdown__body">
        {values.status.length > 0 && (
          <>
            <div className="bp-project-list-filter-dropdown__header">
              <BodyMedium01>
                <FormattedMessage id="projectList/filterDropdown/status"/>
              </BodyMedium01>
            </div>

            <div className="bp-project-list-filter-dropdown__section">
              <div className="bp-project-list-filter-dropdown__filter">
                <HelperText01>
                  <FormattedMessage id="projectList/filterDropdown/statusFilter"/>
                </HelperText01>

                <ProjectListFilterGroup
                  field={form.field.at('status')}
                  options={values.status}
                  previewOptionsLength={4}
                  renderOption={(option) => <ProjectStatusTag projectStatus={option}/>}
                  onApply={onApply}
                />
              </div>
            </div>
          </>
        )}

        <div className="bp-project-list-filter-dropdown__header">
          <BodyMedium01>
            <FormattedMessage id="projectList/filterDropdown/project"/>
          </BodyMedium01>
        </div>

        <div className="bp-project-list-filter-dropdown__section">
          {values.buildingType.length > 0 && (
            <div className="bp-project-list-filter-dropdown__filter">
              <HelperText01>
                <FormattedMessage id="projectList/filterDropdown/buildingType"/>
              </HelperText01>

              <ProjectListFilterGroup
                field={form.field.at('buildingType')}
                options={values.buildingType}
                vertical={true}
                renderOption={renderBuildingTypeOption}
                onApply={onApply}
              />
            </div>
          )}

          {values.city.length > 0 && (
            <div className="bp-project-list-filter-dropdown__filter">
              <HelperText01>
                <FormattedMessage id="projectList/filterDropdown/city"/>
              </HelperText01>

              <ProjectListFilterGroup
                field={form.field.at('city')}
                options={values.city}
                vertical={true}
                renderOption={identity}
                onApply={onApply}
              />
            </div>
          )}

          {values.country.length > 0 && (
            <div className="bp-project-list-filter-dropdown__filter">
              <HelperText01>
                <FormattedMessage id="projectList/filterDropdown/country"/>
              </HelperText01>

              <ProjectListFilterGroup
                field={form.field.at('country')}
                vertical={true}
                options={values.country}
                renderOption={renderCountryCodeOption}
                onApply={onApply}
              />
            </div>
          )}

          {values.ownership.length > 0 && (
            <div className="bp-project-list-filter-dropdown__filter">
              <HelperText01>
                <FormattedMessage id="projectList/filterDropdown/ownership"/>
              </HelperText01>

              <ProjectListFilterGroup
                field={form.field.at('ownership')}
                options={values.ownership.map((owner) => owner.email)}
                vertical={true}
                renderOption={(option) => (
                  authUser.email === option
                    ? intl.formatMessage({ id: 'projectList/item/owned' })
                    : fullNames[option]
                )}
                onApply={onApply}
              />
            </div>
          )}
        </div>

        {hasEfficiencyFilter && (
          <>
            <div className="bp-project-list-filter-dropdown__header">
              <BodyMedium01>
                <FormattedMessage id="projectList/filterDropdown/energyEfficiency"/>
              </BodyMedium01>
            </div>

            <div className="bp-project-list-filter-dropdown__section">
              {values.ratingActual.length > 0 && (
                <div className="bp-project-list-filter-dropdown__filter">
                  <HelperText01>
                    <FormattedMessage id="projectList/filterDropdown/ratingActual"/>
                  </HelperText01>

                  <ProjectListFilterGroup
                    field={form.field.at('ratingActual')}
                    options={values.ratingActual}
                    vertical={true}
                    renderOption={identity}
                    onApply={onApply}
                  />
                </div>
              )}

              {values.ratingFuture.length > 0 && (
                <div className="bp-project-list-filter-dropdown__filter">
                  <HelperText01>
                    <FormattedMessage id="projectList/filterDropdown/ratingFuture"/>
                  </HelperText01>

                  <ProjectListFilterGroup
                    field={form.field.at('ratingFuture')}
                    vertical={true}
                    options={values.ratingFuture}
                    renderOption={identity}
                    onApply={onApply}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="bp-project-list-filter-dropdown__footer">
        <Button type="button" kind="secondary" onClick={handleClear} size="lg">
          <FormattedMessage id="projectList/filterDropdown/clear"/>
        </Button>

        <Button type="button" onClick={onClose} size="lg">
          <FormattedMessage id="projectList/filterDropdown/apply"/>
        </Button>
      </div>
    </div>
  );
};

function renderBuildingTypeOption(option: BuildingType): React.ReactElement {
  return <FormattedMessage id={`common/buildingType/${option}`}/>;
}

function renderCountryCodeOption(option: CountryCode): React.ReactElement {
  return <FormattedMessage id={`common/country/name/${option}`}/>;
}
